import React from "react";
import { Box, Typography, Divider, Container } from "@mui/material";

// Privacy Policy sections data
const sections = [
  {
    title: "1. OUR APPROACH TO PRIVACY",
    paragraphs: [
      "1.1 Law firm data is sensitive. We design LexMom to help law firms operate with clarity while respecting confidentiality and privacy obligations.",
      "1.2 Roles (Important). In many cases, LexMom acts as a service provider / processor for Subscriber Content (data your firm uploads). Your firm (the Subscriber) typically determines what Personal Information is entered into LexMom and is responsible for client notices and consents required by law.",
      "1.3 Privacy Policy + Terms. This Privacy Policy should be read together with LexMom’s Terms of Service.",
    ],
  },
  {
    title: "2. PERSONAL INFORMATION WE COLLECT",
    paragraphs: [
      "We collect Personal Information in three main ways:",
      "2.1 Information you provide to us\nExamples include when you: create an account, start a trial, purchase a plan, or request a demo; contact support (support@lexmom.com); submit forms, surveys, or feedback; enable integrations or configure account settings.\nTypical data types: name, email, phone, firm name, job title; login credentials (stored securely; passwords are not stored in plain text); billing details (processed via Stripe; we do not store full card numbers); communications with us (support tickets, emails, recordings where permitted).",
      "2.2 Information we receive from others\nWe may receive information about you: from your firm’s Administrator if you are added as an Authorized User; from marketing partners (where permitted by law); from Third-Party Services you connect (e.g., email/calendar/storage) based on your settings and authorization.",
      "2.3 Information we collect automatically\nWhen you use the Service, we automatically collect certain information such as: device and browser information (OS, device type, browser version); IP address, approximate location (derived from IP), timestamps; usage and activity logs (pages viewed, features used, clickstream, error logs); cookie identifiers and similar tracking data (see Section 10).",
    ],
  },
  {
    title: "3. HOW WE USE PERSONAL INFORMATION",
    paragraphs: [
      "We use Personal Information for the following purposes:",
      "3.1 Provide and operate the Service\ncreate and administer accounts; provide features, workflows, and access controls; deliver requested services, support, and troubleshooting; authenticate users and prevent fraud/abuse.",
      "3.2 Billing and payments (Stripe)\nprocess subscriptions, invoices, and payments through Stripe; prevent fraud, manage chargebacks/disputes, comply with financial requirements.",
      "3.3 Security and reliability\nmonitor for suspicious activity; maintain logs, prevent malware and abuse; backup and disaster recovery.",
      "3.4 Product improvement and analytics\nunderstand feature usage; improve performance and user experience; develop new features (including AI features) and test changes. Where feasible, we use aggregated and/or de-identified information for these purposes.",
      "3.5 Communications\nsend service-related emails (security alerts, billing notices, product updates); respond to inquiries and support requests; send marketing communications if you opt in or where permitted by law (you can opt out anytime).",
      "3.6 Legal compliance\ncomply with legal obligations and lawful requests; enforce our Terms and protect rights and safety.",
    ],
  },
  {
    title: "4. AI FEATURES — PRIVACY TERMS (IMPORTANT)",
    paragraphs: [
      "LexMom may offer optional AI Features that generate Output based on AI Input you provide.",
      "4.1 What AI data we process\nIf you use AI Features, we may process: AI Input (prompts, selected matter text, documents, or other data you choose); Output (generated summaries, drafts, suggestions, insights); related Usage Data (feature usage, performance, and safety monitoring).",
      "4.2 Human review is your responsibility\nAI Output can be inaccurate. Your firm is responsible for reviewing Output before use, especially for legal work, client communications, filings, and professional obligations.",
      "4.3 AI training and improvement\nWe may use de-identified and aggregated AI Input/Output to improve AI feature quality (e.g., safety, relevance, reliability). We do not use Subscriber Confidential Information to train generalized AI models in a way that identifies you or your clients, except with your explicit permission, as required by law, or if a clearly disclosed setting/feature states otherwise.",
      "4.4 Third-party AI providers\nSome AI Features may be powered by third-party vendors. When used, we share only what is necessary to provide AI Features and we require contractual safeguards appropriate to the Service.",
    ],
  },
  {
    title: "5. DISCLOSURE OF PERSONAL INFORMATION",
    paragraphs: [
      "We do not sell your Personal Information. We may share Personal Information in the following circumstances:",
      "5.1 Service providers and subprocessors\nWe share information with vendors who help us run the Service, such as: cloud hosting and infrastructure; customer support tools; analytics and monitoring providers; email/SMS delivery providers; security vendors; Stripe for payment processing. These providers are authorized to use Personal Information only as needed to provide services to us and are subject to confidentiality and security obligations.",
      "5.2 Integrations you enable\nIf you connect Third-Party Services (e.g., Google/Microsoft, storage, calendars), we share information as needed to operate the integration based on your settings and permissions.",
      "5.3 Legal and safety\nWe may disclose information: to comply with law, court orders, subpoenas, or lawful requests; to protect rights, property, and safety of LexMom, users, or others; to prevent fraud, abuse, or security threats.",
      "5.4 Business transfers\nIf LexMom is involved in a merger, acquisition, financing, reorganization, or sale, Personal Information may be shared as part of that transaction. We will notify you as required by law.",
      "5.5 Public areas (if any)\nIf we host public forums, community pages, or public social media, information you post may be visible to others. (If LexMom does not operate such areas, you may remove this section.)",
    ],
  },
  {
    title: "6. MARKETING PREFERENCES",
    paragraphs: [
      "6.1 We may send marketing emails where permitted.",
      "6.2 You can opt out anytime by clicking unsubscribe in emails or contacting support@lexmom.com.",
      "6.3 You will still receive service-related and transactional messages (e.g., password resets, billing receipts, security alerts).",
    ],
  },
  {
    title: "7. SECURITY",
    paragraphs: [
      "7.1 We use administrative, technical, and physical safeguards designed to protect Personal Information.",
      "7.2 No system is 100% secure. You are responsible for keeping your credentials confidential and using strong passwords and account security settings.",
    ],
  },
  {
    title: "8. INTERNATIONAL TRANSFERS",
    paragraphs: [
      "LexMom may process Personal Information in the United States and other locations where we or our service providers operate. Where required (e.g., EU/UK), we use appropriate safeguards such as contractual protections and data processing agreements.",
    ],
  },
  {
    title: "9. DATA RETENTION",
    paragraphs: [
      "We retain Personal Information only as long as necessary for: providing the Service; complying with legal obligations; resolving disputes; enforcing agreements; and legitimate business purposes.",
      "After account cancellation/termination, Subscriber Content is typically available for export for 90 days (consistent with our Terms), after which it may be deleted unless retention is required by law.",
    ],
  },
  {
    title: "10. COOKIES AND SIMILAR TECHNOLOGIES",
    paragraphs: [
      "10.1 What we use\nWe use cookies and similar technologies for: essential operation (authentication, security); analytics and performance; functionality (preferences); marketing (where permitted).",
      "10.2 Your choices\nYou can control cookies through browser settings. Blocking essential cookies may affect functionality. If required by law (e.g., EU/UK), we will present cookie consent controls.",
      "10.3 Third-party cookies\nSome third parties (analytics/ads) may set cookies. We do not control their cookies; their policies apply.",
    ],
  },
  {
    title: "11. YOUR PRIVACY RIGHTS",
    paragraphs: [
      "Depending on your location, you may have rights to: access your Personal Information; correct inaccurate information; delete Personal Information (subject to exceptions); restrict or object to processing; portability (receive a copy in a usable format); opt out of certain marketing; non-discrimination for exercising rights (where applicable).",
      "To exercise rights, contact support@lexmom.com. We may verify your identity before responding.",
      "If you are an Authorized User in a firm account: your firm (Subscriber) may control your account data and requests may need to be handled through your Administrator.",
    ],
  },
  {
    title: "12. CHILDREN",
    paragraphs: [
      "The Service is not intended for children under 16, and we do not knowingly collect Personal Information from children.",
    ],
  },
  {
    title: "13. CHANGES TO THIS PRIVACY POLICY",
    paragraphs: [
      "We may update this Privacy Policy from time to time. If changes are material, we will provide notice (e.g., email or in-app). The “Last Updated” date will reflect the effective date.",
    ],
  },
  {
    title: "14. CONTACT US",
    paragraphs: [
      "For questions, requests, or privacy concerns, contact:",
      "LexMom Privacy Team\nEmail: support@lexmom.com (or privacy@lexmom.com if you create a dedicated inbox)\nCompany: Menteso, Inc.\nAddress: 589, S 22nd St. San Jose, CA 95116, USA",
    ],
  },
];

const PrivacyPolicy = () => {
  return (
    <Container maxWidth="md" sx={{ py: 8 }}>
      <Typography variant="h3" gutterBottom fontWeight={700}>
        LEXMOM PRIVACY POLICY
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Last Updated: [Insert Date]
      </Typography>
      <Typography paragraph>
        Menteso, Inc. (and its affiliates) (“LexMom,” “we,” “our,” or “us”) is
        committed to protecting your privacy. This Privacy Policy explains how
        we collect, use, store, disclose, and otherwise process information that
        identifies you or can reasonably be linked to you (“Personal
        Information”), and how we use cookies and similar technologies.
      </Typography>
      <Typography paragraph>
        This Privacy Policy applies to LexMom’s websites (including lexmom.com),
        applications, and the cloud-based LexMom platform and related services
        (collectively, the “Service”).
      </Typography>
      <Typography paragraph>
        By accessing or using the Service, you acknowledge you have read and
        understood this Privacy Policy.
      </Typography>

      <Divider sx={{ my: 4 }} />

      {/* Sections */}
      {sections.map((section, idx) => (
        <Box key={idx} sx={{ mt: 4 }}>
          <Typography variant="h5" gutterBottom fontWeight={600}>
            {section.title}
          </Typography>
          {section.paragraphs.map((para, i) => (
            <Typography key={i} paragraph sx={{ whiteSpace: "pre-line" }}>
              {para}
            </Typography>
          ))}
        </Box>
      ))}
    </Container>
  );
};

export default PrivacyPolicy;